import React, { useEffect, useState } from 'react';
import { Button, StatusMessage as CosmaStatusMessage } from 'is24-corecss';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';
import TeamMembersTable from './components/teamMembersTable/TeamMembersTable';
import { TeamMember } from '../../models/TeamMember';
import { fetchTeamMembers, fetchTeamMembersStatistics } from '../../api/TeamMemberApi';
import TeamMemberModal from './components/teamMemberModal/TeamMemberModal';
import DeleteModal from './components/deleteModal/DeleteModal';
import { useFetching } from '../../common/hooks/useFetching';
import { usePopState } from '../../common/hooks/usePopState';
import AddTeamMemberButton from './components/addTeamMemberButton/AddTeamMemberButton';
import RevokeInvitationModal from './components/revokeInvitationModal/RevokeInvitationModal';
import InviteTeamMemberModal from './components/inviteTeamMemberModal/InviteTeamMemberModal';
import InvitationLinkCopyModal from './components/invitationLinkCopyModal/InvitationLinkCopyModal';
import { Team } from '../../models/Team';
import StartAccountPersonalizationModal from './components/startAccountPersonalizationModal/StartAccountPersonalizationModal';
import styles from './styles.module.css';
import { StatusMessageType } from './types/StatusMessageType';
import { getErrorMessage } from '../../common/statusMesssages';
import { notify } from './components/statusMessage/StatusMessage';
import 'react-toastify/dist/ReactToastify.css';
import './styles.css';
import Statistics from './components/statistics/Statistics';
import CompleteTeamReviewModal from './components/completeTeamReviewModal/CompleteTeamReviewModal';
import { companyDataUrl } from '../../common/config';
import { useReportViewEvent } from '../../tracking/hooks';
import { reportActionEvent } from '../../tracking/tracking';

type ModalDialogId =
  | 'invite'
  | 'teammember-form'
  | 'delete-team-member'
  | 'invitation-sent'
  | 'revoke-invitation'
  | 'copy-invitation'
  | 'start-account-personalization'
  | 'complete-team-review'
  | null;

interface TeamManagementProps {
  team: Team;
  onReviewCompleted: (status?: StatusMessageType) => void;
}

const TeamManagement: React.FC<TeamManagementProps> = ({ team, onReviewCompleted }: TeamManagementProps) => {
  const PAGE_VIEW_PATH = 'is24.de.account.verwaltung.firmendaten.teammanagement';

  useReportViewEvent({
    completePath: PAGE_VIEW_PATH,
  });
  const [currentPageNr, setCurrentPageNr] = useState(0);
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<string>('asc');
  const [pageRefresher, setPageRefresher] = useState<boolean>(false);
  const { data: page, error: pageError } = useFetching(fetchTeamMembers, [currentPageNr, sortColumn, sortOrder, pageRefresher]);
  const { data: statistics, error: statsError } = useFetching(fetchTeamMembersStatistics, [pageRefresher]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const query = useQuery();
  const [selectedTeamMember, setSelectedTeamMember] = useState<TeamMember | null>(null);
  const [visibleModal, setVisibleModal] = useState<ModalDialogId>(null);

  usePopState((event) => {
    const maybePage = event?.state?.page || 0;
    setCurrentPageNr(maybePage);
  });

  function useQuery() {
    return new URLSearchParams(useLocation().search);
  }

  useEffect(() => {
    if (query.get('personalisierungAbgeschlossen') === 'true') {
      notify({
        titleCode: 'teammanagement.statusMessage.team.personalization.success.title',
        messageCode: 'teammanagement.statusMessage.team.personalization.success.text',
        messageType: 'confirm',
      });
      reportActionEvent({
        completePath: PAGE_VIEW_PATH,
        label: 'primary_account_setup',
        event_name: 'teammanagement_primary_account_setup',
      });
    }
  }, []);

  useEffect(() => {
    if (!pageError) {
      setTeamMembers(page?.content || []);
    } else {
      notify(getErrorMessage(pageError));
    }
  }, [page, pageError]);

  useEffect(() => {
    if (statsError) {
      notify(getErrorMessage(statsError));
    }
  }, [statsError]);

  const handleCloseDialog = () => {
    setSelectedTeamMember(null);
    setVisibleModal(null);
  };
  const handleSortClick = (column: string) => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    setSortColumn(column);
  };

  const handleRefreshAndCloseDialog = () => {
    setPageRefresher(!pageRefresher);
    handleCloseDialog();
  };

  const handleModalActionResult = (status?: StatusMessageType) => {
    if (status) {
      notify(status);
      if (status.messageType !== 'error') {
        setPageRefresher(!pageRefresher);
      }
    }
    handleCloseDialog();
  };

  const handleCompleteReview = (status?: StatusMessageType) => {
    handleCloseDialog();
    onReviewCompleted(status);
  };

  const handleInviteResult = (status?: StatusMessageType) => {
    if (status?.messageType !== 'error') {
      const app: StatusMessageType = {
        titleCode: 'Einladung wurde erneut gesendet',
        messageType: 'confirm',
        details: 'Das Teammitglied wurde erneut eingeladen. Diese Einladung ist 30 Tage gültig.',
      };
      notify(app);
      handleRefreshAndCloseDialog();
    } else {
      notify(status);
      handleCloseDialog();
    }
  };

  const handleTeamMemberOptionClick = (modalDialogId: ModalDialogId) => (teamMember: TeamMember) => {
    setSelectedTeamMember(teamMember);
    setVisibleModal(modalDialogId);
  };

  const handleAddTeamMemberButtonClick = () => {
    setSelectedTeamMember(null);
    setVisibleModal('teammember-form');
  };

  const handleStartPersonalizationButtonClick = () => {
    setSelectedTeamMember(null);
    setVisibleModal('start-account-personalization');
  };

  const handleCompleteReviewButtonClick = () => {
    setSelectedTeamMember(null);
    setVisibleModal('complete-team-review');
  };

  return (
    <>
      <nav className='grid-item one-whole align-left palm-hide lap-hide margin-top-xs'>
        <a className='font-lightgray icon-arrow-back' href={companyDataUrl()}>
          Meine Firmendaten
        </a>
      </nav>
      <h1>
        <FormattedMessage id='teammanagement.heading' />
      </h1>
      <div className='three-fourth'>
        Legen Sie alle Teammitglieder an, die einen Zugang zu Ihrem ImmoScout24-Kundenkonto benötigen. Diese erhalten eigene Zugangsdaten. Hierdurch
        wird auch die Sicherheit ihrer Daten erhöht, sowie dem Konto mehr Schutz vor Phishing geboten. Wichtiger Hinweis für die CRM-Systemnutzung:
        Die Übertragung über Ihre API-Schnittstelle ist ausschließlich mit den Zugangsdaten Ihres Hauptaccounts möglich. Mehr Informationen finden Sie
        <a href='https://www.immobilienscout24.de/anbieten/gewerbliche-anbieter/lp/sicherheit/' target='_blank' rel='noopener noreferrer'>
          {' '}
          hier
        </a>
        .
      </div>
      <div className='margin-bottom-m' />
      <>
        {['PERSONALIZATION_AVAILABLE', 'PERSONALIZATION_STARTED'].includes(team.status) && (
          <div className='margin-bottom-m '>
            <CosmaStatusMessage
              headline='Wichtiger Hinweis: Sie haben Ihr Konto noch nicht gesichert'
              message={
                <p className={styles.notification}>
                  Erst nachdem Sie die Zugangsdaten auch für Ihr Hauptkonto angepasst haben, sind Ihre Daten geschützt.
                  <br />
                  <Button
                    className='padding-none'
                    textStyle={true}
                    data-testid='account-personalization-button'
                    onClick={handleStartPersonalizationButtonClick}
                  >
                    Jetzt Zugangsdaten ändern
                  </Button>
                </p>
              }
              statusType='error'
              statusStyle='toast'
            />
          </div>
        )}
        {team.status === 'IN_REVIEW' && (
          <div className='margin-bottom-m'>
            <CosmaStatusMessage
              headline='Verifizieren Sie bitte Ihre Benutzerkonten'
              message={
                <p className={styles.notification}>
                  Nachdem Ihr Konto wieder aktiviert ist, muss die Sicherheit aller Unterkonten überprüft werden. <br />
                  Bitte stellen Sie sicher, dass bei allen Unterkonten die korrekten Namen und E-Mail Adressen hinterlegt sind. <br />
                  Mit dem Klick auf <i>&quot;Unterkonten wieder aktivieren&quot;</i> stimmen Sie zu, dass alle Angaben korrekt sind. Alle Unterkonten
                  erhalten automatisch eine E-Mail, um das Passwort neu zu setzen.
                  <br />
                  <Button
                    className='margin-bottom-m margin-top-m'
                    appearance='primary'
                    textStyle={false}
                    data-testid='complete-review-button'
                    onClick={handleCompleteReviewButtonClick}
                  >
                    Unterkonten wieder aktivieren
                  </Button>
                  <br />
                  Sollten Ihnen Unstimmigkeiten auffallen, kontaktieren Sie bitte unser Serviceteam <br />
                  Montag bis Freitag von 8-18 Uhr unter +49 30 2430 11100 <br />
                  An Wochenenden und Feiertagen von 10-18 Uhr unter +49 30 24301 1500
                </p>
              }
              statusType='info'
              statusStyle='toast'
            />
          </div>
        )}
        {statistics && <Statistics statistics={statistics} />}
        {team.status !== 'IN_REVIEW' && <AddTeamMemberButton onAddTeamMember={handleAddTeamMemberButtonClick} />}
        {!!teamMembers.length && (
          <TeamMembersTable
            currentPageNr={currentPageNr}
            totalPages={page?.totalPages || 0}
            pageSize={page?.pageSize || 5}
            teamMembers={teamMembers}
            onMemberDeletion={handleTeamMemberOptionClick('delete-team-member')}
            onMemberUpdate={handleTeamMemberOptionClick('teammember-form')}
            onInvite={handleTeamMemberOptionClick('invite')}
            onRevokeInvitation={handleTeamMemberOptionClick('revoke-invitation')}
            onPageChange={setCurrentPageNr}
            onSort={handleSortClick}
            sortOrder={sortOrder}
            sortColumn={sortColumn}
            onCopyInvitationLink={handleTeamMemberOptionClick('copy-invitation')}
          />
        )}
      </>
      <div className='font-hint margin-top-s'>
        <p className='font-hint font-bold'>Sie haben Fragen?</p>
        <p className='font-hint'>
          +49 30 24301 1900 oder per E-Mail an <a href={'mailto:sicherheit@scout24.com'}>sicherheit@scout24.com</a>
        </p>
      </div>
      <StartAccountPersonalizationModal
        visible={visibleModal === 'start-account-personalization'}
        onClose={handleCloseDialog}
        onPersonalizationStarted={handleModalActionResult}
      />
      <CompleteTeamReviewModal
        visible={visibleModal === 'complete-team-review'}
        onClose={handleCloseDialog}
        onTeamReviewCompleted={handleCompleteReview}
      />
      <TeamMemberModal
        visible={visibleModal === 'teammember-form'}
        onClose={handleCloseDialog}
        selectedTeamMember={selectedTeamMember}
        onCreate={handleModalActionResult}
        onUpdate={handleModalActionResult}
      />
      <DeleteModal
        visible={visibleModal === 'delete-team-member'}
        onClose={handleCloseDialog}
        onDelete={handleModalActionResult}
        selectedTeamMember={selectedTeamMember}
        command='delete'
      />
      <RevokeInvitationModal
        visible={visibleModal === 'revoke-invitation'}
        onClose={handleCloseDialog}
        selectedTeamMember={selectedTeamMember}
        onRevokeInvitation={handleModalActionResult}
      />
      <InviteTeamMemberModal
        visible={visibleModal === 'invite'}
        onClose={handleCloseDialog}
        onInvite={handleInviteResult}
        selectedTeamMember={selectedTeamMember}
      />
      <InvitationLinkCopyModal visible={visibleModal === 'copy-invitation'} selectedTeamMember={selectedTeamMember} onClose={handleCloseDialog} />
    </>
  );
};
export default TeamManagement;
